import { NgIf } from '@angular/common';
import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { Breadcrumb } from '@app/api/core/decorator/breadcrumb.decorator';
import { GsmEditChecklistDialogComponent } from '@app/shared/components/dialog/gsm-edit-checklist-dialog/gsm-edit-checklist-dialog.component';
import { MainContentComponent } from '@app/shared/components/main-content/main-content.component';
import { MenuCardComponent } from '@app/shared/components/menu-card/menu-card.component';
import { ChecklistItemListComponent } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/checklist-item-list/checklist-item-list.component';
import { FormHistoryComponent } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/form-history/form-history.component';
import { FormComponent } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/form/form.component';
import { FormChange, FormModelData, FormSave } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/form/form.model';
import { GsmFatalError } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error';
import { GsmErrorService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error.service';
import { GsmService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm.service';
import { RouterService } from '@app/shared/services/router.service';
import { ActionButton } from '@app/shared/services/ui/dialog/dialog.types';
import { ReactiveDialogService } from '@app/shared/services/ui/reactive-dialog.service';
import { Benutzer as BenutzerGsm, ChecklistInputItem } from '@gsm/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EVENT_TYPE_CHECKBOX_KONNEX, EVENT_TYPE_NAVIGATE, EVENT_TYPE_SCHADENNUMMER, FileUploadResponse, FormDefinitionModel, FormUpdatedFileInstanceResponse, SchadennummerEventModel } from 'app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/shared-models';
import { checkForSpecialCharacters, getAllFileInstances, getStrictCustomEventDetail, removeFileInstances, replaceSpecialCharacters, updateFileInstances } from 'app/shared/gsm/gsm-ui-lib-elements/src/utils';
import { firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PageVertriebscockpitComponent } from '../../page/page.vertriebscockpit.component';
import { EventService } from '../event.service';

@Breadcrumb({
  name: 'Schaden melden'
})

@UntilDestroy()
@Component({
  selector:      'app-schaden-melden',
  templateUrl:   './schaden-melden.component.html',
  styleUrls:     ['./schaden-melden.component.scss'],
  schemas:       [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  standalone:    true,
  imports:       [
    MainContentComponent,
    NgIf,
    MatCard,
    MatButton,
    FormHistoryComponent,
    FormComponent,
    MatProgressSpinner,
    MenuCardComponent,
    MatIcon,
    ChecklistItemListComponent
  ]
})
export class SchadenMeldenComponent extends PageVertriebscockpitComponent implements OnInit {
  private routerService = inject(RouterService);
  private gsmService = inject(GsmService);
  private route = inject(ActivatedRoute);
  private reactiveDialogService = inject(ReactiveDialogService);
  private gsmErrorService = inject(GsmErrorService);
  private eventService = inject(EventService);

  formData: FormModelData[] = [];
  formDefinitionModel: FormDefinitionModel = {} as FormDefinitionModel;
  formHistoryModel: FormDefinitionModel[] = [];
  polizzennummer = '';
  schadendatum;
  schadennummer = '';
  processInstanceId = '';
  isLocked = false;
  isLoading = false;
  isUploading: boolean;
  uploadBytesLoaded = 0;
  uploadBytesTotal = 0;
  submitTitle = 'Weiter';
  showBackButton = false;
  benutzer: BenutzerGsm;
  hasLoadError = false;
  hasUnsavedChanges = false;
  showKonnexBtn = false;
  hasKonnex = false;
  initLoadingError = false;
  checklistItemList: ChecklistInputItem[];
  isAbschluss = false;
  stage: string;

  loadingTaskId: string = null;
  uploadingTaskId: string = null;
  uploadingTaskBytesLoaded = 0;
  uploadingTaskBytesTotal = 0;

  private _selectedTaskId: string = null;
  private _formInputFromStore: FormDefinitionModel;
  private _cancelUpload$ = new Subject();
  private _isScrolling = false;

  @Input() personId: number;

  @ViewChild('leftContainer') leftContainer: ElementRef;
  @ViewChild('gsmForm', {read: ElementRef}) gsmForm: ElementRef;
  @ViewChild('gsmFormHistory', {read: ElementRef}) gsmFormHistory: ElementRef;

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(e: KeyboardEvent) {
    if (!this.isNextBtnDisabled && !this.showBackButton && isCtrlEnter(e)) {
      e.preventDefault();
      this.nextTask()
        .catch(err => this.gsmErrorService.catchError(err));
    }
    if (!this.isLoading) {
      this.hasUnsavedChanges = true;
    }
  }

  get selectedTaskId() {
    return this._selectedTaskId;
  }

  set selectedTaskId(val: string) {
    this._selectedTaskId = val;
    this.llapi.patchValue('gsm.selectedTaskId', val);
    if (!val) {
      this.llapi.patchValue('gsm.currentHistoryFormInput', null);
    }
  }

  get isNextBtnDisabled(): boolean {
    return this.isLocked || this.isLoading || !!this.selectedTaskId;
  }

  constructor() {
    super();

    this.llapi.patchValue('layout.pageLayout.breadcrumb', true);
    this.llapi.patchValue('layout.pageLayout.routerOutletBg', false);
    this.llapi.patchValue('layout.spinner', false);

    this.schadennummer = this.route.snapshot.queryParams?.schadenNr;

    this.route.params.pipe(
        first(),
        untilDestroyed(this)
      )
      .subscribe(data => {
        if (data.polizzennummer) {
          this.polizzennummer = data.polizzennummer;
        }
        if (data.schadendatum) {
          this.schadendatum = data.schadendatum;
        }
        if (data.processInstanceId) {
          this.processInstanceId = data.processInstanceId;
        }
      });
  }

  ngOnInit(): void {
    this.stage = this.llapi.getValue('config.env.stage');
    // Remove later
    this.llapi.getValue$('gsm')
      .pipe(
        first(),
        untilDestroyed(this))
      .subscribe(
        async (gsmData) => {
          if (!gsmData) {
            this.routerService.routeRoot();
            // TODO show Error via message service
          }
          if (gsmData && gsmData.benutzer) {
            this.benutzer = gsmData.benutzer;
          }
          if (gsmData && gsmData.currentFormInput) {
            this._formInputFromStore = gsmData.currentFormInput;
          }
          if (!this.route.snapshot.params.processInstanceId && gsmData && gsmData.processInstanceId) {
            this.processInstanceId = gsmData.processInstanceId;
          }
          if (this.processInstanceId) {
            await this.nextTask()
              .catch(err => this.gsmErrorService.catchError(err));
            await this._getTaskHistory(this.processInstanceId)
              .catch(err => this.gsmErrorService.catchError(err));
          } else {
            await this.initForm()
              .catch(err => this.gsmErrorService.catchError(err));
          }
          if (gsmData && gsmData.selectedTaskId && gsmData.currentHistoryFormInput) {
            await this.onHistoryEdit(gsmData.selectedTaskId, gsmData.currentHistoryFormInput);
          }
        });
  }

  async updateChecklist(): Promise<void> {
    if (this.processInstanceId) {
      const checklistItemList$ = this.gsmService.getChecklistItemList(this.processInstanceId)
        .pipe(this.gsmErrorService.handleErrorAndReturnArray());
      const checklistItemList = await firstValueFrom(checklistItemList$);

      this.checklistItemList = this._sortChecklist(checklistItemList);
    }
  }

  private _sortChecklist(checklistItemList: ChecklistInputItem[]): ChecklistInputItem[] {
    const formHistoryLayoutIds = this.formHistoryModel.map(value => value && value.layoutId);
    const sortedChecklistItemList = [];
    for (const layoutId of formHistoryLayoutIds) {
      const checklistItems = checklistItemList.filter(checklistItem => checklistItem && checklistItem.taskId === layoutId);
      if (checklistItems?.length) {
        sortedChecklistItemList.push(...checklistItems);
      }
    }
    return sortedChecklistItemList;
  }

  async initForm(): Promise<void> {
    let formDefinitionModel: FormDefinitionModel;

    try {
      this.setIsLoading(true);
      const formDefinitionModel$ = this.gsmService.getStartForm('EINSTIEG_ADM');
      formDefinitionModel = await firstValueFrom(formDefinitionModel$);
      this.setIsLoading(false);
    } catch (err) {
      this.initLoadingError = true;
      this.gsmErrorService.catchError(err);
      this.formDefinitionModel = {} as FormDefinitionModel;

      return;
    }

    // create key-value map of form control models, with id as key
    formDefinitionModel.data.forEach(model => {
      switch (model.id) {
        case 'EINSTIEG_FF_polizzenNummer':
          model.value = this.polizzennummer || null;
          return;
        case 'EINSTIEG_FF_schadenDatum':
          model.value = this.schadendatum || null;
          return;
        case 'EINSTIEG_FF_reporterEmail':
          model.value = this.benutzer && this.benutzer.reporterEmail || null;
          return;
        case 'EINSTIEG_FF_reporterRole':
          model.value = this.benutzer && this.benutzer.reporterRole || null;
          return;
        case 'EINSTIEG_FF_reporterId':
          model.value = this.benutzer && this.benutzer.reporterId || null;
          return;
        case 'EINSTIEG_FF_schadennummer':
          model.value = this.schadennummer || null;
          return;
        default:
          return;
      }
    });

    this.formDefinitionModel = formDefinitionModel;
  }

  setIsLoading(value: boolean) {
    // TODO: set hlapi loading spinner
    this.isLoading = value;
  }

  handleMessage(event: FormChange): void {
    this.formData = event.data;
    this.formDefinitionModel.data = event.formModel;
    this.isLocked = event.formInvalid;

    this.llapi.patchValue('gsm.currentFormInput', removeFileInstances(this.formDefinitionModel));
    this.llapi.patchValue('gsm.lastEditTimestamp', Date.now());

    this._scrollToCurrentTask();
  }

  onSubmit() {
    if (
      this.formDefinitionModel.layoutId &&
      this.formDefinitionModel.layoutId.toLowerCase() ===
      'EINSTIEG_ADM'.toLowerCase()
    ) {
      if (this.formDefinitionModel.data[0].value) {
        const polizzenNr = this.formDefinitionModel.data[0].value;
        if (checkForSpecialCharacters(polizzenNr)) {
          this.formDefinitionModel.data[0].value = replaceSpecialCharacters(
            polizzenNr
          );
        }
      }
    }
    if (!!this.selectedTaskId) {
      if (
        this.formDefinitionModel.layoutId &&
        this.formDefinitionModel.layoutId.toLowerCase() ===
        'ABSCHLUSS_UT_dokumente'.toLowerCase()
      ) {
        this._openAbschlussChecklistDialog();
      } else {
        this._openDisabledTaskDialog();
        this._scrollToSelected();
      }
    } else if (
      this.formDefinitionModel.layoutId &&
      this.formDefinitionModel.layoutId.toLowerCase() ===
      'EINSTIEG_UT_meldungNichtMoeglich'.toLowerCase()
    ) {
      this.initForm()
        .catch(err => this.gsmErrorService.catchError(err));
    } else if (!this.isLocked) {
      this.nextTask()
        .catch(err => this.gsmErrorService.catchError(err));
    }
  }

  async nextTask() {
    this.isLocked = true;
    if (!this.processInstanceId) {
      this.setIsLoading(true);
      try {
        await this._postStartForm();
      } catch (err) {
        this.hasUnsavedChanges = false;
        this.gsmErrorService.catchError(err);
        this.setIsLoading(false);
        return;
      }
    } else if (this.formDefinitionModel.taskId) {
      const fileInstances = await getAllFileInstances(this.formData);
      // has to be set after file instances to allow screenshot to be taken
      this.setIsLoading(true);

      let formData: FormModelData[];
      let uploadError: string;

      try {
        const uploadResult$ = this._uploadFiles(
          this.formData,
          fileInstances
        );
        const uploadResult = await firstValueFrom(uploadResult$);
        // If result is empty, upload was cancelled.
        if (!uploadResult) {
          this.setIsLoading(false);
          this.isLocked = false;
          return;
        }
        formData = uploadResult.formData;
        uploadError = uploadResult.errorMessage;
      } catch (err) {
        this.gsmErrorService.catchError(err);

        this.setIsLoading(false);
        this.isLocked = false;
        return;
      }
      this.formData = formData;

      if (uploadError) {
        this.eventService.openVirusDialog(uploadError);
        this.setIsLoading(false);
        this.isLocked = false;
        return;
      }

      await this._postNextForm();
    }

    // add last step, and filter out empty or bestehender Schaden steps from history
    this.formHistoryModel = (this.formHistoryModel || [])
      .concat(JSON.parse(JSON.stringify(this.formDefinitionModel)))
      .filter(
        value =>
          value &&
          Object.keys(value).length &&
          value.layoutId !== 'EINSTIEG_UT_schadennummerUebernehmen' &&
          value.layoutId !== 'ABSCHLUSS_UT_InfoWeitereSteps'
      );

    if (
      this.formDefinitionModel.layoutId &&
      this.formDefinitionModel.layoutId.toLowerCase() ===
      'EINSTIEG_UT_schadennummerUebernehmen'.toLowerCase()
    ) {
      switch (this._getValueProp('checkListStatus')) {
        case 'T':
        case 'N':
          await this.routerService.routeChecklistItemNachbringen(this.formData[0].value.schadenNummer);
          this._removeLocalStorageLastEditDraft();
          return;
        case 'P':
          this._removeLocalStorageLastEditDraft();
          throw new GsmFatalError('Der gewählte Schaden mit dem Status "prüfbar/vollständig" wurde bereits abgeschlossen.');
      }
      const pid = this._getValueProp('processInstanceId');
      if (pid) {
        // if it's an existing Entwurf, reload task history
        await this._getTaskHistory(pid);
      }
    }

    await this._getNextForm();
    await this.updateChecklist();
    this.setIsLoading(false);
  }

  private _getValueProp(key: string): any {
    return (this.formData && this.formData[0] && this.formData[0].value && this.formData[0].value[key]) || null;
  }

  onCancelUpload(taskId?: string) {
    this._cancelUpload$.next(null);
    this._setUploadStatus(false, 0, 0, taskId);
  }

  private _uploadFiles(
    formData: FormModelData[],
    fileInstances: File[],
    taskId?: string
  ): Observable<FormUpdatedFileInstanceResponse> {
    const bytesTotal = fileInstances.reduce((sum, file) => sum + file.size, 0);
    this._setUploadStatus(true, 0, bytesTotal, taskId);

    return this.gsmService.uploadFiles(this.processInstanceId, fileInstances)
      .pipe(
        takeUntil(this._cancelUpload$),
        tap((event: HttpEvent<FileUploadResponse>) => {
          if (event.type === HttpEventType.UploadProgress) {
            this._setUploadStatus(true, event.loaded, bytesTotal, taskId);
          }
        }),
        filter((event: HttpEvent<FileUploadResponse>) => event.type === HttpEventType.Response),
        tap(() => this._setUploadStatus(false, 0, 0, taskId)),
        map((event: HttpResponse<FileUploadResponse>) =>
          updateFileInstances(formData, event.body)
        )
      );
  }

  private _setUploadStatus(
    isUploading: boolean,
    bytesLoaded: number,
    bytesTotal: number,
    taskId?: string
  ) {
    if (taskId) {
      this.uploadingTaskId = isUploading ? taskId : null;
      this.uploadingTaskBytesLoaded = bytesLoaded;
      this.uploadingTaskBytesTotal = bytesTotal;
    } else {
      this.isUploading = isUploading;
      this.uploadBytesLoaded = bytesLoaded;
      this.uploadBytesTotal = bytesTotal;
    }
  }

  private async _postStartForm() {
    // Before submitting formData, set kundeId to selectedPerson
    const kundeId = this.formData.find(item => item.id === 'EINSTIEG_FF_kundeId');
    if (kundeId) {
      const selectedPerson = this.llapi.getValue('layout.uiData.selectedPerson');
      kundeId.value = selectedPerson?.toString();
    }

    const prozess$ = this.gsmService.postStartForm({
      ...this.formDefinitionModel,
      data: stripType(this.formData)
    });
    const prozess = await firstValueFrom(prozess$);
    this.llapi.patchValue('gsm.processInstanceId', prozess.processInstanceId);

    this.processInstanceId = prozess.processInstanceId;
  }

  private async _postNextForm() {
    if (this.formDefinitionModel.processInstanceId === this.processInstanceId) {
      try {
        const result$ = this.gsmService.postNextTaskForm({
          ...this.formDefinitionModel,
          data: stripType(this.formData)
        });
        await firstValueFrom(result$);
      } catch (err) {
        this.gsmErrorService.catchError(err);
        return;
      }
    }

    this._updateProcessInstanceId();
  }

  private _updateProcessInstanceId() {
    if (
      this.formDefinitionModel.layoutId &&
      this.formDefinitionModel.layoutId.toLowerCase() ===
      'EINSTIEG_UT_schadennummerUebernehmen'.toLowerCase()
    ) {
      const newProcessInstanceId =
        this.formData[0] &&
        this.formData[0].value &&
        this.formData[0].value.processInstanceId;

      if (newProcessInstanceId && newProcessInstanceId !== this.processInstanceId) {
        this.processInstanceId = newProcessInstanceId;
      }
    }
  }

  private async _getNextForm() {
    try {
      const storedInput = this._formInputFromStore;
      const processTask$ = this.gsmService.getNextTaskForm(this.processInstanceId);
      const processTask = await firstValueFrom(processTask$);

      this.formDefinitionModel = {
        ...await this.gsmService.mergeUserTaskAndInput(this.processInstanceId, processTask, storedInput),
        processKeyName: undefined
      };
    } catch (err) {
      this._removeLocalStorageLastEditDraft();
      this.hasLoadError = true;
      this.formDefinitionModel = {} as FormDefinitionModel;
      this.gsmErrorService.catchError(err);
      return;
    }

    this.hasUnsavedChanges = false;

    if (this.formDefinitionModel.layoutId) {
      const layoutId = this.formDefinitionModel.layoutId.toLowerCase();
      if (this.formHistoryModel?.length) {
        this.formHistoryModel = this.formHistoryModel.filter(value => value.layoutId !== this.formDefinitionModel.layoutId);
      }
      if (layoutId === 'ABSCHLUSS_UT_InfoWeitereSteps'.toLowerCase()) {
        this.showBackButton = true;
        this.isAbschluss = true;
        this._removeLocalStorageLastEditDraft();
        this.llapi.patchValue('gsm.processInstanceId', null);

        const inputField = this.formDefinitionModel.data.find(
          model => model.id === 'ABSCHLUSS_FF_konnexSparte'
        );
        // if no konnex, terminate process immediately
        if (inputField.options && !inputField.options.length) {
          await this.terminateProcess(this.formDefinitionModel);
        } else {
          this.hasKonnex = true;
        }
      } else {
        this._updateLocalStorageLastEditDraft();
        if (layoutId === 'ABSCHLUSS_UT_dokumente'.toLowerCase()) {
          this.submitTitle = 'Absenden';
        } else if (
          this.formDefinitionModel.layoutId &&
          this.formDefinitionModel.layoutId.toLowerCase() ===
          'EINSTIEG_UT_meldungNichtMoeglich'.toLowerCase()
        ) {
          await this.terminateProcess(this.formDefinitionModel);
          this._removeLocalStorageLastEditDraft();
          this.formHistoryModel = null;
          this.formData = null;
          this.llapi.patchValue('gsm.processInstanceId', null);
        } else {
          this.submitTitle = 'Weiter';
        }
      }
    }
  }

  async onCustomEvent(event: CustomEvent) {
    const eventType = event && event.type;
    if (eventType === EVENT_TYPE_NAVIGATE) {
      if (this.showKonnexBtn) {
        await this.reportKonnex();
      } else {
        await this.navigateToSchadenuebersicht();
      }
    } else if (eventType === EVENT_TYPE_CHECKBOX_KONNEX) {
      this.showKonnexBtn = !!(getStrictCustomEventDetail(EVENT_TYPE_CHECKBOX_KONNEX, event));
    } else if (eventType === EVENT_TYPE_SCHADENNUMMER) {
      const schadennummer = (event as CustomEvent<SchadennummerEventModel>)?.detail?.schadennummer;
      if (schadennummer) {
        this.llapi.patchValue('gsm.schadenNummer', schadennummer);
      }
    } else {
      this.eventService.onCustomEvent(event, this.processInstanceId);
    }
  }

  private _updateLocalStorageLastEditDraft() {
    this.llapi.patchValue('gsm.processInstanceId', this.processInstanceId);
    this.llapi.patchValue('gsm.lastEditTimestamp', Date.now());
  }

  private _removeLocalStorageLastEditDraft() {
    this.clearProcessDraft();
  }

  public async navigateToSchadenuebersicht() {
    if (this.schadennummer) {
      this.routerService.routeGSMSchadenUebersicht(this.schadennummer);
    } else {
      const schadennummer = this.llapi.getValue('gsm.schadenNummer');
      this.schadennummer = removeSpecialCharacters(schadennummer);
      this.routerService.routeGSMSchadenUebersicht(this.schadennummer);
    }
  }

  async reportKonnex() {
    // build the formDefModel as soon as the konnex-checkbox is checked
    if (this.showKonnexBtn && this.formData.length) {
      this.llapi.patchValue('layout.spinner', true);

      // go one process step further and start konnexReport in pe
      const result$ = this.gsmService.postNextTaskForm({
          ...this.formDefinitionModel,
          data: stripType(this.formData)
        })
        .pipe(
          // get the new konnex-processInstanceId
          switchMap(() => this.gsmService.getNextTaskForm(this.formDefinitionModel.processInstanceId)),
          switchMap((formDefinitionModel: FormDefinitionModel) => {
            const konnexProcessInstanceId = formDefinitionModel.data[0].additional.konnexProcessInstanceId;

            return this.startKonnexProcess(konnexProcessInstanceId, formDefinitionModel);
          }),
          // handle error
          catchError(err => {
            this.gsmErrorService.catchError(err);
            return of();
          })
        );
      await firstValueFrom(result$);
    }
  }

  startKonnexProcess(konnexProcessInstanceId: string, priorFormDefModel: FormDefinitionModel): Observable<void> {
    // set konnex-processInstanceId in store
    this.llapi.patchValue('gsm.processInstanceId', konnexProcessInstanceId);

    // terminates prior Process
    return this._postTerminateProcess(priorFormDefModel)
      .pipe(
        // update state
        tap(() => {
          this._resetDefaultValues();
          this.processInstanceId = konnexProcessInstanceId;

          this.llapi.patchValue('layout.spinner', false);
        }),
        // perform next task
        switchMap(() => from(this.nextTask()))
      );
  }

  private _resetDefaultValues() {
    this.processInstanceId = '';
    this.showKonnexBtn = false;
    this.showBackButton = false;
    this.hasKonnex = false;
    this.isAbschluss = false;
    this.isLocked = false;
    this.hasUnsavedChanges = false;
    this.initLoadingError = false;
    this.submitTitle = 'Weiter';
    this.formHistoryModel = [];
  }

  async terminateProcess(formDefinitionModel: FormDefinitionModel): Promise<void> {
    if (this.processInstanceId) {
      const result$ = this._postTerminateProcess(formDefinitionModel);
      await firstValueFrom(result$);
      this.processInstanceId = null;
    } else {
      return null;
    }
  }

  private _postTerminateProcess(formDefinitionModel: FormDefinitionModel): Observable<boolean> {
    const closingFormDefModel: FormDefinitionModel = {
      taskId:            formDefinitionModel.taskId,
      layoutId:          formDefinitionModel.layoutId,
      processInstanceId: formDefinitionModel.processInstanceId,
      data:              null
    };
    return this.gsmService.postNextTaskForm(closingFormDefModel);
  }

  deleteProcess(processInstanceId: string): Observable<boolean> {
    return this.gsmService.deleteProcess(processInstanceId);
  }

  async onSelectChecklistItem(cli: ChecklistInputItem) {
    // this is actually layoutId
    if (cli.taskId) {
      const taskModel = this.formHistoryModel.find(
        model => model.layoutId === cli.taskId
      );
      if (taskModel) {
        await this.onHistoryEdit(taskModel.taskId);
      }
    }
  }

  private _openDisabledTaskDialog() {
    const dialogRef = this._openChecklistDialog([
      {
        name:  'Zurück',
        color: 'accent',
        value: 'cancelled'
      }
    ], 'Bitte schließen Sie die aktive Checklisten-Bearbeitung ab!');
    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
          if (result && result.status === 'cancelled') {
            dialogRef.close();
          }
        }
      );
  }

  private _openAbschlussChecklistDialog() {
    const dialogRef = this._openChecklistDialog([
      {
        name:  'Bestätigen',
        color: 'accent',
        value: 'success'
      },
      {
        name:  'Abbrechen',
        color: 'mat-gray-bg',
        value: 'cancelled'
      }
    ], 'Ein Prozessschritt befindet sich noch in Bearbeitung. Möchten Sie trotzdem Fortfahren? Nicht gespeicherte Änderungen gehen verloren!');
    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result.status === 'cancelled') {
          dialogRef.close();
        } else {
          this.onHistoryCancel()
            .catch(err => this.gsmErrorService.catchError(err));
          this.nextTask()
            .catch(err => this.gsmErrorService.catchError(err));
        }
      });
  }

  private _openChecklistDialog(actionButtons: ActionButton[], dialogText: string): MatDialogRef<GsmEditChecklistDialogComponent> {
    return this.reactiveDialogService.open(GsmEditChecklistDialogComponent, {
      maxWidth: '800px',
      data:     {
        title:        'Warnung',
        buttonConfig: {
          visible: true,
          actionButtons
        }
      }
    }, dialogText);
  }

  async onHistoryEdit(taskId: string, storedInput?: FormDefinitionModel) {
    if (!taskId) {
      return;
    }

    if (!this.selectedTaskId) {
      this.selectedTaskId = taskId;

      // Important to scroll first before triggering re-render of form history, otherwise scroll position can get wonky!
      await this._scrollToSelected();

      const newModel = await this._requestProcessTaskUpdate(taskId);

      // Re-render of form history is triggered here
      this.formHistoryModel = await Promise.all(this.formHistoryModel.map(async model =>
        model.taskId === taskId ? await this.gsmService.mergeUserTaskAndInput(this.processInstanceId, newModel, storedInput) : model
      ));
    } else if (this.selectedTaskId !== taskId) {
      this._openDisabledTaskDialog();
      await this._scrollToSelected();
    }
  }

  async onHistoryChange(event: FormChange) {
    if (event.taskId !== this.selectedTaskId) {
      return;
    }
    const originalFormHistoryModel = this.formHistoryModel.find(entry => entry.taskId === this.selectedTaskId);
    const currentFormHistoryModel = {
      ...originalFormHistoryModel,
      data: event.formModel
    };

    this.llapi.patchValue('gsm.currentHistoryFormInput', removeFileInstances(currentFormHistoryModel));

    await this._scrollToSelected();
  }

  async onHistorySave(taskData: FormSave) {

    try {
      await this._updateProcessTask(taskData);
    } catch (err) {
      // TODO: decide what to do with internal state and add doc comment
      return;
    }

    this.loadingTaskId = null;
  }

  async onHistoryCancel() {
    this.selectedTaskId = null;
  }

  private async _requestProcessTaskUpdate(
    taskId: string
  ): Promise<FormDefinitionModel> {
    try {
      const result$ = this.gsmService.getRequestProcessTaskUpdate(taskId);
      return await firstValueFrom(result$);
    } catch (err) {
      return null;
    }
  }

  private async _getTaskHistory(processInstanceId: string) {
    const response$ = this.gsmService.getProcessTaskHistory(processInstanceId)
      .pipe(this.gsmErrorService.handleErrorAndReturnNull());
    const response = await firstValueFrom(response$);

    if (response) {
      // remove step bestehender Schaden from History
      this.formHistoryModel = response.filter(value => value && Object.keys(value).length && value.layoutId !== 'EINSTIEG_UT_schadennummerUebernehmen');
    }
  }

  /**
   *
   * @param formDefinitionModel
   * @param data
   * @private
   *
   * @throws Error if upload errors occur e.g. virus scan positive
   */
  private async _updateProcessTask({
                                     formDefinitionModel,
                                     data
                                   }: FormSave): Promise<void> {
    const fileInstances = await getAllFileInstances(data);
    // has to be set after file instances to allow screenshot to be taken
    this.loadingTaskId = formDefinitionModel.taskId;

    let formData: FormModelData[];
    let uploadError: string;

    try {
      const uploadResult$ = this._uploadFiles(
        data,
        fileInstances,
        formDefinitionModel.taskId
      );
      const uploadResult = await firstValueFrom(uploadResult$);
      // If result is empty, upload was cancelled.
      if (!uploadResult) {
        return;
      }
      formData = uploadResult.formData;
      uploadError = uploadResult.errorMessage;
    } catch (err) {
      this.gsmErrorService.catchError(err);
      return;
    }

    if (uploadError) {
      this.eventService.openVirusDialog(uploadError);
      throw new Error(uploadError);
    }

    const response$ = this.gsmService.updateProcessTask({
        ...formDefinitionModel,
        data: formData
      })
      .pipe(this.gsmErrorService.handleErrorAndReturnNull());
    const response = await firstValueFrom(response$);

    // return on error
    if (!response) {
      return;
    }

    if (response.pathChanged) {
      const taskIndex = this.formHistoryModel.findIndex(
        model => model.taskId === formDefinitionModel.taskId
      );

      if (response.newTaskId) {
        this.formHistoryModel = this.formHistoryModel.map(
          model => {
            if (model.taskId === formDefinitionModel.taskId) {
              model.taskId = response.newTaskId;
              formDefinitionModel.taskId = response.newTaskId;
            }
            return model;
          }
        );
      }

      // if path changed, cut off form after the updated task and fetch next task
      this.formHistoryModel = this.formHistoryModel
        .slice(0, taskIndex)
        .concat(formDefinitionModel);

      await this._getNextForm();
    } else {
      // otherwise replace the updated task
      this.formHistoryModel = this.formHistoryModel.map(model =>
        model.taskId === formDefinitionModel.taskId
          ? formDefinitionModel
          : model
      );
    }

    this.selectedTaskId = null;

    await this.updateChecklist();
  }

  private async _scrollToCurrentTask() {
    // Wait until the next tick in order to give Angular a chance to run change detection.
    await new Promise((resolve) => setTimeout(resolve, 100));
    return this._scrollToElement(this.gsmForm.nativeElement);
  }

  private async _scrollToSelected() {
    // Wait in order to give Angular a chance to run change detection.
    await new Promise(resolve => setTimeout(resolve, 0));
    return this._scrollToElement(
      this.gsmFormHistory
      && this.gsmFormHistory.nativeElement
      && this.gsmFormHistory.nativeElement.querySelector('#gsm-selected-task'),
      true
    );
  }

  private async _scrollToElement(element?: HTMLElement, allowScrollUp = false) {
    // Prevent double scrolls. This can happen for instance if the user is editing the current task,
    // then clicks on a checklist item - the currently edited field triggers a change event, which causes
    // an additional scroll event to the current task.
    if (!element || this._isScrolling) {
      return;
    }

    this._isScrolling = true;

    // Get parent of left container == actual scroll container.
    const container = this.leftContainer && this.leftContainer.nativeElement.parentElement;
    const containerDimensions = container.getBoundingClientRect();

    // Get diff between top of form and top of container, in order to know how much to scroll.
    const diff = element.getBoundingClientRect().top - containerDimensions.top;

    // If allowScrollUp is false, only scroll down - do not scroll back up when top of form is above top of container.
    if (allowScrollUp || diff > 0) {
      // Scroll position shouldn't be bigger than what's actually possible given the visible container size.
      // Otherwise, the scroll effect might not occur next time a new element is added.
      const scrollPosition = Math.min(
        container.scrollTop + diff,
        container.scrollHeight - containerDimensions.height
      );

      await scrollToPosition(container, scrollPosition);
    }

    this._isScrolling = false;
  }

  // TODO move process draft info to a separate object for more clarity
  clearProcessDraft() {
    this.llapi.resetValues([
      'gsm.processInstanceId',
      'gsm.lastEditTimestamp',
      'gsm.selectedTaskId',
      'gsm.currentFormInput',
      'gsm.currentHistoryFormInput'
    ]);
  }
}

// Element.scrollTo() doesn't offer a built-in way to find out when it's finished,
// so we need to provide our own.
async function scrollToPosition(container: Element, position: number) {
  const roundPosition = Math.round(position);

  if (container.scrollTop === roundPosition) {
    return;
  }

  let resolveFn;
  let scrollListener;
  let timeoutId;

  const promise = new Promise(resolve => {
    resolveFn = resolve;
  });

  const finished = () => {
    container.removeEventListener('scroll', scrollListener);
    resolveFn();
  };

  scrollListener = () => {
    clearTimeout(timeoutId);

    // scroll is finished when either the position has been reached, or 100ms have elapsed since the last scroll event
    if (container.scrollTop === roundPosition) {
      finished();
    } else {
      timeoutId = setTimeout(finished, 100);
    }
  };

  container.addEventListener('scroll', scrollListener);

  container.scrollTo({
    top:      roundPosition,
    behavior: 'smooth'
  });

  return promise;
}

function isCtrlEnter(e: KeyboardEvent): boolean {
  // is CTRL+Enter clicked ?
  return e.code === 'Enter' && e.ctrlKey;
}

function stripType(data: FormModelData[]): FormModelData[] {
  if (data) {
    data.forEach(d => {
      delete d.type;
    });
  }
  return data;
}

function removeSpecialCharacters(stringvalue: string): string {
  return stringvalue.replace(/[^\d]/g, '');
}
